import { SortAscIcon, SortDescIcon } from '@primer/octicons-react';
import { Box, IconButton } from '@primer/react';
import { Column, Header, Row, Table, flexRender } from '@tanstack/react-table';
import { tableV2HeaderTestId } from '@upright/test-ids/table-v2';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { themeGet } from '@upright/ui/common/generic/theme/custom-theme';
import { CssFromFunction } from '@upright/ui/common/generic/types/types';
import { isNil, max } from 'lodash';
import { CSSProperties } from 'react';
import styled, { css } from 'styled-components';
import { TableStyleProps, TableV2ColumnEventHandlers, TableV2Variant } from '../../types';
import { getFirstAndLastColumnIds } from '../../utils/utils';
import { TableCellCommonStyleProps, getTableCellCommonStyle } from './table-cell-common';

export function THead<T extends object, ColumnId extends string>(props: {
  table: Table<T>;
  getColumnProps?: (column: Column<T>) => Partial<TableStyleProps>;
  getHeaderProps?: (column: Column<T>) => Partial<TableStyleProps>;
  hideSortIcons?: boolean;
  eventHandlers?: TableV2ColumnEventHandlers<ColumnId>;
  'data-testid'?: string;
  showVerticalBorders: boolean;
  variant: TableV2Variant;
  onRowClick?: (row: Row<T>) => void;
}) {
  const { firstColumnId, lastColumnId } = getFirstAndLastColumnIds(props.table);

  return (
    <TheadRoot>
      {props.table.getHeaderGroups().map(headerGroup => (
        <Tr key={headerGroup.id}>
          {headerGroup.headers.map(header => {
            const column = header.column;
            const columnDef = column.columnDef;
            const headerDef = columnDef.header;

            const meta = columnDef.meta;
            const headerElement = typeof headerDef === 'string' ? <ColumnTitle>{headerDef}</ColumnTitle> : headerDef;

            const columnCanSort = column.getCanSort();
            const sortDirection = column.getIsSorted();

            const columnProps = props.getColumnProps?.(column);
            const headerProps = props.getHeaderProps?.(column);

            const sortingHandler = column.getToggleSortingHandler();
            const onClick = props.eventHandlers?.onSortingChange;

            const align = meta?.titleAlign || meta?.align || 'left';

            // How many layers deep current header is under the same exact column
            const columnRelativeDepth = header.depth - column.depth;
            if (columnRelativeDepth > 1) {
              return null; // Is rendered by the multiple row spanning header directly above
            }
            // Take maximum of relative depths under this header
            const rowSpan = calculateRowDepth(header);
            const isHeaderOfLastColumn = header.column.id === lastColumnId;

            // If there are overlapping style props, the hierarchy is as follows:
            // headerProps > columnProps > columnDef.meta.headerCss > columnDef.meta.css.
            const mergedStyle: CSSProperties = { ...(columnProps?.style || {}), ...(headerProps?.style || {}) };

            const mergedCss: CssFromFunction = [
              `grid-row: span ${rowSpan};`,
              `grid-column: span ${header.colSpan};`,
              ...(meta?.css || []),
              ...(meta?.headerCss || []),
              ...(columnProps?.css || []),
              ...(headerProps?.css || []),
            ];
            return (
              <Th
                style={mergedStyle}
                css={mergedCss}
                metaCss={meta?.css}
                align={align}
                isFirstColumn={column.id === firstColumnId}
                isLastColumn={isHeaderOfLastColumn}
                $variant={props.variant}
                key={header.id}
                data-testid={headerProps?.['data-testid'] ?? tableV2HeaderTestId(props['data-testid'], header.id)}
                colSpan={header.colSpan} // Does nothing, for debug purposes
                rowSpan={rowSpan} // Does nothing, for debug purposes
                showVerticalBorders={props.showVerticalBorders && !isHeaderOfLastColumn}
              >
                <HeaderCellWrap
                  $canSort={columnCanSort}
                  onClick={event => {
                    if (onClick) onClick(header.id as ColumnId, sortDirection === 'desc');
                    if (sortingHandler) sortingHandler(event);
                  }}
                >
                  <div>{flexRender(headerElement, header.getContext())}</div>
                  {!props.hideSortIcons && sortDirection && (
                    <Box width={28} height={28}>
                      <IconButton
                        variant="invisible"
                        size="small"
                        aria-label="Sort column"
                        icon={sortDirection === 'asc' ? SortAscIcon : SortDescIcon}
                      />
                    </Box>
                  )}
                </HeaderCellWrap>
              </Th>
            );
          })}
        </Tr>
      ))}
    </TheadRoot>
  );
}

// Shows possibly multiple header rows underneath each other
const TheadRoot = styled.thead`
  /* Don't add any styling here, it won't matter because of display: contents */
  display: contents;
`;

const Tr = styled.tr<{ css?: CssFromFunction }>`
  /* Don't add any styling here, it won't matter because of display: contents */
  display: contents;
`;

export const Th = styled.th<TableCellCommonStyleProps & { $variant?: TableV2Variant }>`
  ${props =>
    getTableCellCommonStyle({
      align: props.align,
      isFirstColumn: props.isFirstColumn,
      isLastColumn: props.isLastColumn,
    })};
  ${props =>
    props.showVerticalBorders
      ? css`
          border-right: 1px solid ${BouColor.PLATFORM_BORDER_GRAY_LIGHTER};
        `
      : ''}
  border-bottom: 0.5px solid ${BouColor.PLATFORM_BG_GRAY_LIGHT};
  /* The two variants differ in the header fill and its weight, and in nothing else. The hairline
  under the row is what separates the header from the body in both, so a header on the page
  background still reads as a header. See TableV2Variant in ../../types. */
  ${props =>
    props.$variant === 'plain'
      ? css`
          font-weight: 600;
          background-color: var(--color__white);
        `
      : css`
          font-weight: 500;
          background-color: ${BouColor.PLATFORM_BG_GRAY_LIGHTER};
        `}
  padding-top: ${themeGet('space.1')};
  padding-bottom: ${themeGet('space.1')};
  min-height: 3.1em;
  ${props => props.css};
`;

const HeaderCellWrap = styled.div<{ $canSort?: boolean }>`
  display: flex;
  align-items: center;
  max-width: 100%;
  height: 100%;
  cursor: ${props => (props.$canSort ? 'pointer' : 'default')};
`;

const ColumnTitle = styled.span`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

function calculateRowDepth<T>(header: Header<T, unknown>): number {
  if (!header.isPlaceholder) {
    return 1;
  }
  const leafs = header.getLeafHeaders();
  const maximumDepthUnderHeader = max(leafs.map(leaf => leaf.depth - leaf.column.depth));
  if (isNil(maximumDepthUnderHeader)) {
    throw Error('Unexpected: Placeholder header should have leaf headers under it.');
  }
  return maximumDepthUnderHeader;
}
